import React, { useState } from 'react';
import { toast } from 'sonner';
import { adminApi, apiError, API } from '../../lib/adminApi';
import { FLAVOURS } from '../../data/mock';
import SchedulePicker from './SchedulePicker';
import PostStats from './PostStats';
import { Copy, Download, Trash2, Instagram, Facebook, Loader2, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';

const btn = 'inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/15 text-white/80 hover:border-red-500 hover:text-white disabled:opacity-40 transition-colors';
const nameOf = (f) => (f === 'mix' ? 'MIX BOX' : FLAVOURS[f]?.name);
const colorOf = (f) => (f === 'mix' ? '#FF2D2D' : FLAVOURS[f]?.primary);

function Pending({ post, remove }) {
  return (
    <div className="bg-[#0d0d0d] border border-white/10 rounded-xl aspect-square flex flex-col items-center justify-center gap-3 text-white/50 text-sm p-4 text-center"
      data-testid={`social-post-${post.status}`}>
      {post.status === 'generating'
        ? <><Loader2 className="animate-spin text-red-500" /> {post.kind === 'video' ? 'Video' : 'Post'} {nameOf(post.flavour)} in creazione…
          {post.kind === 'video' && <span className="text-xs text-white/30">I video richiedono 3-8 minuti</span>}</>
        : <><AlertTriangle className="text-yellow-400" /> Creazione non riuscita. <button className={btn} onClick={remove}>Rimuovi</button></>}
    </div>
  );
}

export default function SocialPostCard({ post, metaOk, reload }) {
  const [busy, setBusy] = useState('');
  const text = `${post.caption}\n\n${(post.hashtags || []).join(' ')}`;
  const isVideo = post.kind === 'video';
  const media = `${API}/social/media/${post.id}.${isVideo ? 'mp4' : 'jpg'}`;

  const publish = async (target) => {
    setBusy(target);
    try {
      await adminApi.post(`/admin/social/posts/${post.id}/publish`, { targets: [target] });
      toast.success(`Pubblicato su ${target === 'instagram' ? 'Instagram' : 'Facebook'}`);
      reload();
    } catch (e) { toast.error(apiError(e)); }
    setBusy('');
  };
  const remove = async () => { await adminApi.delete(`/admin/social/posts/${post.id}`); reload(); };
  const copy = () => { navigator.clipboard.writeText(text); toast.success('Testo copiato'); };

  if (post.status === 'generating' || post.status === 'failed') return <Pending post={post} remove={remove} />;

  return (
    <div className="bg-[#0d0d0d] border border-white/10 rounded-xl overflow-hidden flex flex-col" data-testid="social-post-card">
      {isVideo
        ? <video src={media} poster={`${API}/social/media/${post.id}.jpg`} controls playsInline className="w-full aspect-[9/16] max-h-[460px] bg-black object-cover" data-testid="social-post-video" />
        : <img src={media} alt={post.flavour} className="w-full aspect-square object-cover" />}
      <div className="p-4 flex-1 flex flex-col gap-3">
        <div className="flex items-center justify-between text-xs">
          <span className="font-bold" style={{ color: colorOf(post.flavour) }}>
            {nameOf(post.flavour)} · {isVideo ? 'Reel video' : post.style === 'graphic' ? 'Grafico' : 'Realistico'}
          </span>
          {post.status === 'published' && <span className="flex items-center gap-1 text-green-400"><CheckCircle2 size={12} /> Pubblicato</span>}
          {post.status !== 'published' && post.scheduled_at && (
            <span className="flex items-center gap-1 text-sky-300" data-testid="post-scheduled-badge"><Clock size={12} /> Programmato</span>
          )}
        </div>
        {post.schedule_error && <p className="text-yellow-300/90 text-xs" data-testid="post-schedule-error">{post.schedule_error}</p>}
        <p className="text-white/80 text-sm whitespace-pre-line line-clamp-6" data-testid="social-post-caption">{post.caption}</p>
        <p className="text-red-400/80 text-xs">{(post.hashtags || []).join(' ')}</p>
        <PostStats stats={post.stats} />
        {post.status !== 'published' && <SchedulePicker post={post} reload={reload} />}
        <div className="flex flex-wrap gap-2 mt-auto">
          <button className={btn} onClick={copy} data-testid="post-copy-btn"><Copy size={14} /> Copia testo</button>
          <a className={btn} href={media} download={`sman-${post.flavour}.${isVideo ? 'mp4' : 'jpg'}`} target="_blank" rel="noreferrer" data-testid="post-download-btn"><Download size={14} /> Scarica</a>
          <button className={btn} disabled={!metaOk || !!busy} onClick={() => publish('instagram')} data-testid="post-publish-instagram" title={metaOk ? '' : 'Collega prima Meta'}>
            {busy === 'instagram' ? <Loader2 size={14} className="animate-spin" /> : <Instagram size={14} />} Instagram
          </button>
          <button className={btn} disabled={!metaOk || !!busy} onClick={() => publish('facebook')} data-testid="post-publish-facebook" title={metaOk ? '' : 'Collega prima Meta'}>
            {busy === 'facebook' ? <Loader2 size={14} className="animate-spin" /> : <Facebook size={14} />} Facebook
          </button>
          <button className={btn} onClick={remove} data-testid="post-delete-btn"><Trash2 size={14} /></button>
        </div>
      </div>
    </div>
  );
}
